import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import AdminFilterBar from './components/AdminFilterBar';
import AdminDataTable from './components/AdminDataTable';
import FreshnessBadge from './components/FreshnessBadge';
import { fetchAdminVenues } from '../../../services/Http/AdminApi';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';

const AdminVenues = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [virtualSource, setVirtualSource] = useState('');
  const [apiActive, setApiActive] = useState('');
  const requestSeqRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setLoadError('');
    try {
      const params = { per_page: 50 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (virtualSource) params.virtual_source = virtualSource;
      if (apiActive !== '') params.is_api_active = apiActive;

      // Debug trace for admin venues page lifecycle.
      console.log('[AdminVenues] loading', params);
      const resp = await fetchAdminVenues(params);

      // Ignore stale responses when typing quickly.
      if (seq !== requestSeqRef.current) return;
      setRows(resp?.data || []);
    } catch (error) {
      if (seq !== requestSeqRef.current) return;
      console.error('[AdminVenues] load failed', error);
      setLoadError(error?.response?.data?.message || error?.message || 'Failed to load venues');
      toast.error(error?.response?.data?.message || 'Failed to load venues');
    } finally {
      if (seq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [debouncedSearch, virtualSource, apiActive]);

  useEffect(() => {
    console.log('[AdminVenues] mounted');
    load();
  }, [load]);

  return (
    <PageContainer>
      <PageHeader><PageTitle>Venues</PageTitle></PageHeader>
      <PageContent>
        <AdminFilterBar>
          <Row>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search venue/org/city" />
            <Select value={virtualSource} onChange={(e) => setVirtualSource(e.target.value)}>
              <option value="">All sources</option>
              <option value="demo">demo</option>
              <option value="manual">manual</option>
              <option value="import">import</option>
              <option value="scanned_bill">scanned_bill</option>
            </Select>
            <Select value={apiActive} onChange={(e) => setApiActive(e.target.value)}>
              <option value="">Any API status</option>
              <option value="true">API active</option>
              <option value="false">API inactive</option>
            </Select>
            <FreshnessBadge status="success" sourceType="operational" />
          </Row>
          <MainButton size="small" onClick={load}>Refresh</MainButton>
        </AdminFilterBar>

        {loadError && (
          <ErrorBanner>{loadError}</ErrorBanner>
        )}

        <AdminDataTable
          loading={loading}
          rows={rows}
          emptyText="No venues found"
          columns={[
            { key: 'name', label: 'Venue', render: (r) => <RowLink to={`/venues/${r.id}`}>{r.name || '—'}</RowLink> },
            { key: 'organization_name', label: 'Organization' },
            { key: 'city', label: 'City' },
            { key: 'website', label: 'Website', render: (r) => r.website || '—' },
            { key: 'is_api_active', label: 'API', render: (r) => <Flag $tone={r.is_api_active ? 'success' : 'neutral'}>{r.is_api_active ? 'Active' : 'Inactive'}</Flag> },
            { key: 'integrations_count', label: 'Integrations' },
            { key: 'virtual_source', label: 'Source', render: (r) => r.virtual_source ? <Flag $tone={r.virtual_source === 'demo' ? 'warning' : 'neutral'}>{r.virtual_source}</Flag> : 'real' },
            { key: 'updated_at', label: 'Updated', render: (r) => r.updated_at ? new Date(r.updated_at).toLocaleString() : '—' },
          ]}
        />
      </PageContent>
    </PageContainer>
  );
};

export default AdminVenues;

const Row = styled.div`display:flex;gap: 0.625rem;align-items:center;flex-wrap:wrap;`;
const SearchInput = styled.input`width:300px;border:1px solid ${({theme})=>theme.colors.border};border-radius:10px;padding: 0.5625rem 0.75rem;background:${({theme})=>theme.colors.surface};`;
const Select = styled(SimpleSelect)`border:1px solid ${({theme})=>theme.colors.border};border-radius:10px;padding: 0.5625rem 0.75rem;background:${({theme})=>theme.colors.surface};`;
const RowLink = styled(Link)`color:var(--color-primary);text-decoration:none;font-weight:600;`;
const Flag = styled.span`
  display:inline-flex;align-items:center;border-radius:999px;padding: 0.25rem 0.5rem;font-size: 0.75rem;font-weight:700;text-transform:capitalize;
  background:${({theme,$tone}) => $tone === 'success' ? theme.colors.successBg : $tone === 'warning' ? theme.colors.warningBg : theme.colors.surfaceMuted};
  color:${({theme,$tone}) => $tone === 'success' ? theme.colors.success : $tone === 'warning' ? theme.colors.warning : theme.colors.textMuted};
`;
const ErrorBanner = styled.div`
  margin-bottom: 0.75rem;
  border: 1px solid ${({theme}) => theme.colors.dangerBg || '#fecaca'};
  background: ${({theme}) => theme.colors.dangerBg || '#fef2f2'};
  color: ${({theme}) => theme.colors.danger || '#991b1b'};
  border-radius: 10px;
  padding: 0.625rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
`;
